import {
  getDb,
  markDirty,
  queryAll,
  queryOne,
  run,
  writeAudit,
} from '@/lib/db'
import { computeDualAxisWaveform, evaluateQualification } from '@/lib/compute'
import { DEPLOYMENT_HOST, PROJECT_SLUG } from '@/lib/db/constants'

const DEMO_PASSWORDS = {
  'admin@softwarelink.net': 'Admin@2026!Sec',
  'auditor@softwarelink.net': 'Auditor@2026!Sec',
}

function encodeSession(user) {
  const payload = btoa(unescape(encodeURIComponent(JSON.stringify({
    ...user,
    exp: Date.now() + 12 * 60 * 60 * 1000,
  }))))
  return `fb.${payload}`
}

export function decodeSession(token) {
  if (!token || !token.startsWith('fb.')) return null
  try {
    const raw = decodeURIComponent(escape(atob(token.slice(3))))
    const data = JSON.parse(raw)
    if (!data.exp || data.exp < Date.now()) return null
    return {
      id: data.id,
      email: data.email,
      displayName: data.displayName,
      role: data.role,
      organizationType: data.organizationType,
    }
  } catch {
    return null
  }
}

function getBearer(headers) {
  const h = headers?.Authorization || ''
  return h.startsWith('Bearer ') ? h.slice(7).trim() : null
}

function requireRole(user, roles) {
  if (!user) {
    const err = new Error('未授权：请先登录')
    err.status = 401
    throw err
  }
  if (!roles.includes(user.role)) {
    const err = new Error('权限不足：当前角色无法访问此资源')
    err.status = 403
    throw err
  }
  return user
}

function parseDeadline(isoLike) {
  const normalized = isoLike.includes('T') ? isoLike : `${isoLike.replace(' ', 'T')}+08:00`
  return new Date(normalized).getTime()
}

function countdown(targetMs) {
  const diff = Math.max(0, targetMs - Date.now())
  return {
    expired: diff === 0,
    remainingMs: diff,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  }
}

function mapUser(row) {
  if (!row) return null
  return {
    id: row.id,
    email: row.email,
    displayName: row.display_name,
    role: row.role,
    organizationType: row.organization_type,
  }
}

function verifyPassword(email, password, row) {
  const demo = DEMO_PASSWORDS[email.toLowerCase()]
  if (demo && demo === password) return true
  if (row?.password_hash === `demo:${password}`) return true
  return false
}

/**
 * Browser sql.js API — replaces Worker / D1 endpoints.
 * @returns {Promise<object|null>}
 */
export async function handleApi(path, { method = 'GET', body, headers } = {}) {
  getDb()
  const user = decodeSession(getBearer(headers))
  const role = user?.role ?? 'GUEST'

  if (path === '/api/health') {
    return {
      ok: true,
      service: PROJECT_SLUG,
      host: DEPLOYMENT_HOST,
      slug: PROJECT_SLUG,
      runtime: 'browser-sqljs-r2',
      ts: new Date().toISOString(),
    }
  }

  if (path === '/api/auth/login' && method === 'POST') {
    const email = String(body?.email || '').toLowerCase()
    const row = queryOne(
      `SELECT id, email, password_hash, display_name, role, organization_type FROM fb_users WHERE email = ?`,
      [email],
    )
    if (!row || !verifyPassword(email, body?.password, row)) {
      writeAudit(null, 'AUTH_LOGIN_FAILED', { email })
      const err = new Error('账号或密码错误')
      err.status = 401
      throw err
    }
    const sessionUser = mapUser(row)
    writeAudit(sessionUser.id, 'AUTH_LOGIN_SUCCESS', { email })
    return {
      ok: true,
      token: encodeSession(sessionUser),
      user: sessionUser,
      caSignMock: {
        algorithm: 'SM2-with-SM3',
        signer: sessionUser.displayName,
        signedAt: new Date().toISOString(),
        digest: `CA-${sessionUser.id}-${Date.now().toString(16)}`,
      },
    }
  }

  if (path === '/api/auth/guest' && method === 'POST') {
    const guest = {
      id: 'usr_guest_anon',
      email: 'guest@local',
      displayName: '访客 (Guest)',
      role: 'GUEST',
      organizationType: 'DOMESTIC',
    }
    return { ok: true, token: encodeSession(guest), user: guest }
  }

  if (path === '/api/auth/me' && method === 'GET') {
    if (!user) {
      const err = new Error('会话无效或已过期')
      err.status = 401
      throw err
    }
    return { ok: true, user }
  }

  if (path === '/api/auth/logout' && method === 'POST') {
    if (user) writeAudit(user.id, 'AUTH_LOGOUT', {})
    markDirty()
    return { ok: true }
  }

  if (path === '/api/tender' && method === 'GET') {
    const row = queryOne(`SELECT * FROM fb_tender_items WHERE project_no = ?`, ['0730-269010010004/01'])
    const saleEnd = parseDeadline(String(row.doc_sale_end))
    const bidEnd = parseDeadline(String(row.bid_deadline))
    const techPoints = [
      '高刚度双轴加载机架与超高温加热环境仓（轴向动态拉压 + 同轴高频双向扭转）',
      '硬件控制器与多轴疲劳测试控制分析软件必须同一制造商原装同源开发',
      '多通道亚毫秒级高速采样、动态相位解耦与 DIC / 高温引伸计闭环接口',
      '关境外投标人须具备外贸合同签署与合规收汇能力，并提供近3个月银行资信证明',
    ]
    return {
      ok: true,
      role,
      tender: {
        projectNo: row.project_no,
        title: row.title,
        buyerName: row.buyer_name,
        agencyName: row.agency_name,
        deliveryLocation: row.delivery_location,
        deliveryPeriodMonths: row.delivery_period_months,
        docFeeRmb: row.doc_fee_rmb,
        docFeeUsd: row.doc_fee_usd,
        status: row.status,
        summary: row.summary,
      },
      countdown: {
        documentSale: countdown(saleEnd),
        bidDeadline: countdown(bidEnd),
      },
      milestones: [
        { key: 'publish', label: '公告发布', at: '2026-08-01 14:37:31', done: true },
        { key: 'doc_sale_start', label: '招标文件发售开始', at: row.doc_sale_start, done: true },
        { key: 'doc_sale_end', label: '招标文件发售截止', at: row.doc_sale_end, done: Date.now() > saleEnd },
        { key: 'bid_deadline', label: '投标截止 / 开标', at: row.bid_deadline, done: Date.now() > bidEnd },
        { key: 'delivery', label: '合同签订后交货期', at: '签约后 8 个月内', done: false },
      ],
      techPoints: role === 'GUEST' ? techPoints.slice(0, 2) : techPoints,
      changeStream: role === 'GUEST'
        ? [{ at: '2026-08-01', note: '公告公开发布（公开摘要）' }]
        : [
            { at: '2026-08-01 14:37:31', note: '国际公开招标公告发布' },
            { at: '2026-08-01 09:00:00', note: '招标文件开始发售（人民币500 / 美元75）' },
            { at: '2026-08-11 16:00:00', note: '招标文件发售截止节点' },
            { at: '2026-08-24 09:30:00', note: '投标截止与开标节点' },
          ],
    }
  }

  if (path === '/api/tender/export' && method === 'GET') {
    requireRole(user, ['LAB_ENGINEER'])
    const items = queryAll(`SELECT * FROM fb_tender_items`)
    writeAudit(user.id, 'TENDER_EXPORT', { count: items.length })
    return { ok: true, exportedAt: new Date().toISOString(), items }
  }

  if (path === '/api/fatigue/presets' && method === 'GET') {
    const presets = queryAll(
      `SELECT id, preset_name, material_grade, temp_celsius, axial_load_kn, torsion_torque_nm, frequency_hz, phase_shift_deg, created_by, created_at FROM fb_fatigue_simulations ORDER BY created_at`,
    )
    const isGuest = role === 'GUEST'
    return {
      ok: true,
      mode: isGuest ? 'basic_readonly' : role === 'LAB_ENGINEER' ? 'expert' : 'full',
      presets: isGuest
        ? presets.map((p) => ({
            id: p.id,
            preset_name: p.preset_name,
            material_grade: p.material_grade,
            temp_celsius: p.temp_celsius,
            axial_load_kn: p.axial_load_kn,
            frequency_hz: p.frequency_hz,
          }))
        : presets,
    }
  }

  if (path === '/api/fatigue/calc' && method === 'POST') {
    const isGuest = role === 'GUEST'
    const input = {
      axialLoadKn: Number(body.axialLoadKn),
      torsionTorqueNm: Number(body.torsionTorqueNm),
      frequencyHz: Number(body.frequencyHz),
      phaseShiftDeg: isGuest ? 0 : Number(body.phaseShiftDeg),
      tempCelsius: isGuest ? Math.min(Number(body.tempCelsius), 650) : Number(body.tempCelsius),
      samples: isGuest ? 180 : 720,
    }
    const result = computeDualAxisWaveform(input)
    if (user && user.role !== 'GUEST') {
      writeAudit(user.id, 'FATIGUE_CALC', input)
    }
    return {
      ok: true,
      role,
      input: body,
      result,
      dllMapping: role === 'LAB_ENGINEER'
        ? {
            driver: 'FatigueCtrl_C_DLL_v3.so',
            exports: ['FAT_InitAxis', 'FAT_SetWaveform', 'FAT_ArmPhaseLock', 'FAT_StartDualAxis'],
            channelMap: { CH0: 'Fz_axial_kN', CH1: 'Mz_torsion_Nm', CH2: 'T_furnace_C' },
          }
        : undefined,
    }
  }

  if (path === '/api/fatigue/export' && method === 'POST') {
    requireRole(user, ['LAB_ENGINEER'])
    const spectrum = computeDualAxisWaveform(body)
    writeAudit(user.id, 'FATIGUE_SPECTRUM_EXPORT', { presetName: body?.presetName })
    return { ok: true, r2Key: `spectra/${Date.now()}.json`, spectrum }
  }

  if (path === '/api/qualification/check' && method === 'POST') {
    requireRole(user, ['AUDITOR', 'LAB_ENGINEER'])
    const evaluation = evaluateQualification({
      bidderName: body.bidderName,
      bidderType: body.bidderType,
      hasManufacturerAuth: !!body.hasManufacturerAuth,
      hasBankCreditCert: !!body.hasBankCreditCert,
      hasForeignTradeContractCapacity: !!body.hasForeignTradeContractCapacity,
      hasCreditChinaViolation: !!body.hasCreditChinaViolation,
      bankCertMonths: body.bankCertMonths ?? 3,
    })
    const id = `qual_${Date.now().toString(36)}`
    run(
      `INSERT INTO fb_qualification_records (
        id, bidder_name, bidder_type, has_manufacturer_auth, has_bank_credit_cert,
        has_foreign_trade_contract_capacity, has_credit_china_violation, compliance_score, audited_by
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        body.bidderName,
        body.bidderType,
        body.hasManufacturerAuth ? 1 : 0,
        body.hasBankCreditCert ? 1 : 0,
        body.hasForeignTradeContractCapacity ? 1 : 0,
        body.hasCreditChinaViolation ? 1 : 0,
        evaluation.complianceScore,
        user.id,
      ],
    )
    writeAudit(user.id, 'QUALIFICATION_CHECK', { id, score: evaluation.complianceScore })
    return { ok: true, id, evaluation, archived: user.role === 'LAB_ENGINEER' }
  }

  if (path === '/api/qualification/records' && method === 'GET') {
    requireRole(user, ['AUDITOR', 'LAB_ENGINEER'])
    return { ok: true, records: queryAll(`SELECT * FROM fb_qualification_records ORDER BY updated_at DESC LIMIT 100`) }
  }

  if (path === '/api/qualification/criteria' && method === 'GET') {
    requireRole(user, ['AUDITOR', 'LAB_ENGINEER'])
    return {
      ok: true,
      criteria: [
        { key: 'manufacturerAuth', label: '制造商授权 / 软硬件同源证明', weight: 0.25, required: true },
        { key: 'bankCredit', label: '近3个月银行资信证明', weight: 0.25, required: true },
        { key: 'foreignTrade', label: '关境外外贸合同与合规收汇能力', weight: 0.2, requiredFor: 'OVERSEAS' },
        { key: 'creditIntegrity', label: '非失信被执行人（信用中国）', weight: 0.2, required: true },
        { key: 'documentCompleteness', label: '投标要件完备度', weight: 0.1, required: true },
      ],
      editable: user.role === 'LAB_ENGINEER',
    }
  }

  if (path === '/api/admin/configs' && method === 'GET') {
    requireRole(user, ['AUDITOR', 'LAB_ENGINEER'])
    return { ok: true, configs: queryAll(`SELECT * FROM fb_system_configs ORDER BY config_key`) }
  }

  if (path === '/api/admin/configs' && method === 'PUT') {
    requireRole(user, ['LAB_ENGINEER'])
    run(
      `UPDATE fb_system_configs SET config_value = ?, updated_at = CURRENT_TIMESTAMP WHERE config_key = ?`,
      [body.config_value, body.config_key],
    )
    writeAudit(user.id, 'CONFIG_UPDATE', body)
    return { ok: true }
  }

  if (path === '/api/admin/audit-logs' && method === 'GET') {
    requireRole(user, ['AUDITOR', 'LAB_ENGINEER'])
    return { ok: true, logs: queryAll(`SELECT * FROM fb_audit_logs ORDER BY id DESC LIMIT 200`) }
  }

  if (path === '/api/admin/seed-reset' && method === 'POST') {
    requireRole(user, ['LAB_ENGINEER'])
    run(`DELETE FROM fb_qualification_records`)
    writeAudit(user.id, 'SEED_RESET', { note: 'fb_qualification_records cleared' })
    return { ok: true, message: '资格自检记录已重置（浏览器 SQLite + R2 同步）' }
  }

  return null
}
